import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useStoreContext } from 'features/store';
import _ from 'lodash';

import { useCart } from 'hooks/use-cart';
import { useEnableUnifiedMenu } from 'hooks/use-enable-unified-menu';
import { actions, selectors, useAppDispatch, useAppSelector } from 'state/global-state';
import { useLoyaltyContext } from 'state/loyalty';
import {
  compareBy_id,
  compareBy_idAndErrors,
  useConditionalState,
} from 'state/loyalty/hooks/use-conditional-state';
import { useLoyaltyOfferGroups } from 'state/loyalty/hooks/use-loyalty-offer-groups';
import { useLoyaltyUserId } from 'state/loyalty/hooks/use-loyalty-user';
import { useMainMenuContext } from 'state/menu/main-menu';
import { IDayPartBoundary } from 'state/menu/main-menu/types';
import { useServiceModeContext } from 'state/service-mode';
import { useUnifiedMenuContext } from 'state/unified-menu/unified-menu-context';

import {
  TransformTierGroupsToOffersOptions_Legacy,
  transformTierGroupsToOffers_Legacy,
} from './tiered-offers/legacy';
import {
  TransformTierGroupsToOffersOptions_UnifiedMenu,
  transformTierGroupsToOffers_UnifiedMenu,
} from './tiered-offers/one-menu';
import { removeSystemwideTierGroups } from './tiered-offers/utils';

export function useGetCmsAndLoyaltyOffers() {
  const [refreshOffersLoading, setRefreshOffersLoading] = useState(false);
  const {
    queryLoyaltyOffers,
    queryLoyaltyUserStandardOffers,
    loyaltyStandardOffersEnabled,
    refetchCmsOffers,
  } = useLoyaltyContext();
  const { loyaltyUserId, loading: loyaltyUserIdLoading } = useLoyaltyUserId();
  const { calculateCartTotal } = useCart();
  const {
    loading: offerGroupsLoading,
    refetch: refetchOfferGroups,
    offerGroups: cmsOfferGroups,
  } = useLoyaltyOfferGroups();
  const dispatch = useAppDispatch();
  const { activeDayParts } = useMainMenuContext();
  const dayPartsRef = useRef({
    setActiveDayPartOnLoad: false,
    prevDayParts: [] as IDayPartBoundary[],
  });

  const _cmsOffers = useAppSelector(selectors.loyalty.selectCmsOffers);
  const [cmsOffers, setCmsOffers] = useConditionalState(
    () => removeSystemwideTierGroups(_cmsOffers),
    compareBy_id
  );
  const { serviceMode } = useServiceModeContext();
  const { store, prices } = useStoreContext();
  const isUnifiedMenuEnabled = useEnableUnifiedMenu();
  const { indexedMenu, isLoading: isUnifiedMenuLoading } = useUnifiedMenuContext();

  const userOffers = useAppSelector(selectors.loyalty.selectUserOffers);
  const _personalizedOffers = useAppSelector(selectors.loyalty.selectPersonalizedOffers);
  const [personalizedOffers, setPersonalizedOffers] = useConditionalState(
    _personalizedOffers,
    compareBy_idAndErrors
  );
  const unauthenticatedOffers = useAppSelector(selectors.loyalty.selectOffers);

  const transformedCmsOffers = useMemo(() => {
    if (isUnifiedMenuEnabled && !isUnifiedMenuLoading && indexedMenu && serviceMode) {
      return transformTierGroupsToOffers_UnifiedMenu({
        items: _cmsOffers as TransformTierGroupsToOffersOptions_UnifiedMenu['items'],
        indexedMenu,
        serviceMode,
      });
    } else if (!isUnifiedMenuEnabled && store._id && prices && serviceMode) {
      return transformTierGroupsToOffers_Legacy({
        items: _cmsOffers as TransformTierGroupsToOffersOptions_Legacy['items'],
        store,
        serviceMode,
        prices,
      });
    }
    return [];
  }, [
    _cmsOffers,
    isUnifiedMenuEnabled,
    isUnifiedMenuLoading,
    indexedMenu,
    serviceMode,
    store,
    prices,
  ]);

  useEffect(() => {
    setCmsOffers(transformedCmsOffers);
  }, [transformedCmsOffers, setCmsOffers]); // setCmsOffers is stable but ESLint doesn't know that

  useEffect(() => {
    setPersonalizedOffers(_personalizedOffers);
  }, [_personalizedOffers, setPersonalizedOffers]); // setPersonalizedOffers is stable but ESLint doesn't know that

  const baseOffers = !loyaltyUserIdLoading && loyaltyUserId ? userOffers : unauthenticatedOffers;

  // This refers to userOffers, personalizedOffers, cmsOffers and unauthenticatedOffers
  const offersLoading = useAppSelector(selectors.loyalty.selectOffersLoading);

  const cartTotal: number = useMemo(() => (calculateCartTotal ? calculateCartTotal() : 0), [
    calculateCartTotal,
  ]);

  const fetchOffers = useCallback(() => {
    // Reset shouldRefetchOffers flag
    dispatch(actions.loyalty.setShouldRefetchOffers(false));

    if (loyaltyStandardOffersEnabled) {
      if (loyaltyUserId) {
        queryLoyaltyUserStandardOffers({ loyaltyId: loyaltyUserId, subtotalAmount: cartTotal });
      } else {
        queryLoyaltyOffers({ subtotalAmount: cartTotal });
      }
    }
  }, [
    cartTotal,
    loyaltyUserId,
    loyaltyStandardOffersEnabled,
    queryLoyaltyOffers,
    queryLoyaltyUserStandardOffers,
    dispatch,
  ]);

  const refreshOffers = useCallback(async () => {
    // When a user manually refreshes we have to fetch the CMS offers
    // and the engine offers. These two offers have been implemented in different
    // ways and signal different loading mechanisms (one is through redux, the other a hook)
    // To standardize the way this component responds to loading, I've opted to
    // manually set the offers loading (which is currently what happens when you call fetchOffers)
    // I'm doing this eagerly so the refresh control doesn't close. FlatList will close the RefreshControl
    // if the refreshing state doesn't get signalled in an appropriate time.

    setRefreshOffersLoading(true);

    await Promise.all([refetchCmsOffers, refetchOfferGroups]).then(fetchOffers);

    setRefreshOffersLoading(false);
  }, [fetchOffers, refetchCmsOffers, refetchOfferGroups]);

  useEffect(() => {
    if (activeDayParts.length === 0) {
      return;
    }

    if (!_.isEqual(activeDayParts, dayPartsRef.current.prevDayParts)) {
      if (!dayPartsRef.current.setActiveDayPartOnLoad) {
        dayPartsRef.current.setActiveDayPartOnLoad = true;
      } else {
        refreshOffers();
      }

      dayPartsRef.current.prevDayParts = activeDayParts;
    }
  }, [activeDayParts, refreshOffers]);

  return {
    cmsOffers,
    cmsOfferGroups,
    personalizedOffers,
    baseOffers,
    offersLoading: offersLoading || offerGroupsLoading,
    refreshOffers,
    refreshOffersLoading,
  };
}
